import React, { useEffect, useCallback } from "react";
import { Box, keyframes } from "@mui/material";
import SpecsHeader from "./specs/SpecsHeader";
import HeroSlide from "./specs/HeroSlide";
import FeatureIcons from "./specs/FeatureIcons";
import QRJoinCard from "./specs/QRJoinCard";
import SlideController, { SlideDots } from "./specs/SlideController";
import AmbientParticles from "./specs/AmbientParticles";
import {
  HERO_SLIDES,
  JOIN_PATH,
  SPECS,
  specsPageSx,
  specsRaySpin,
} from "./specs/specsTheme";

const slideCrossfade = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const lockViewport = () => {
  const html = document.documentElement;
  const body = document.body;
  const prev = {
    htmlOverflow: html.style.overflow,
    bodyOverflow: body.style.overflow,
    bodyHeight: body.style.height,
    bodyOverscroll: body.style.overscrollBehavior,
  };
  html.style.overflow = "hidden";
  body.style.overflow = "hidden";
  body.style.height = "100%";
  body.style.overscrollBehavior = "none";
  return () => {
    html.style.overflow = prev.htmlOverflow;
    body.style.overflow = prev.bodyOverflow;
    body.style.height = prev.bodyHeight;
    body.style.overscrollBehavior = prev.bodyOverscroll;
  };
};

const SpectatorIdle = () => {
  const joinUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${JOIN_PATH}`
      : JOIN_PATH;

  useEffect(() => lockViewport(), []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "MYM — Mind Your Music Live";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const tryFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "f" || e.key === "F") tryFullscreen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tryFullscreen]);

  return (
    <Box
      sx={specsPageSx}
      onDoubleClick={tryFullscreen}
      role="presentation"
    >
      <AmbientParticles />

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "42%",
          left: "32%",
          width: "min(70vw, 70vh)",
          aspectRatio: "1",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 0,
          opacity: 0.16,
          background: `conic-gradient(from 0deg, transparent, ${SPECS.magenta}55, transparent 18%, ${SPECS.gold}44, transparent 36%, ${SPECS.orange}40, transparent 55%, ${SPECS.magenta}35, transparent 72%, ${SPECS.gold}30, transparent)`,
          borderRadius: "50%",
          filter: "blur(2px)",
          animation: `${specsRaySpin} 48s linear infinite`,
        }}
      />

      <SpecsHeader />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gridTemplateRows: "minmax(0, 1fr) auto",
          gap: "clamp(8px, 1.6vh, 28px)",
          alignItems: "stretch",
          overflow: "hidden",
          px: "clamp(12px, 2.5vw, 40px)",
          pb: "clamp(10px, 2vh, 32px)",
          pt: "clamp(2px, 0.6vh, 8px)",
          "@media (min-width: 900px), (max-height: 560px) and (min-width: 700px)": {
            gridTemplateColumns: "minmax(0, 1fr) minmax(180px, 340px)",
            gridTemplateRows: "minmax(0, 1fr)",
          },
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "clamp(6px, 1.4vh, 16px)",
          }}
        >
          <SlideController slides={HERO_SLIDES}>
            {({ slide, index, goTo }) => (
              <>
                <Box
                  key={slide?.id || index}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    animation: `${slideCrossfade} 0.7s ease both`,
                  }}
                >
                  <HeroSlide slide={slide} />
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(6px, 1.2vh, 14px)",
                  }}
                >
                  <SlideDots
                    slides={HERO_SLIDES}
                    index={index}
                    goTo={goTo}
                  />
                  <FeatureIcons />
                </Box>
              </>
            )}
          </SlideController>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <QRJoinCard joinUrl={joinUrl} />
        </Box>
      </Box>
    </Box>
  );
};

export default SpectatorIdle;
